<script lang="ts" setup>
import ANotificationPanelListCard from './components/ANotificationPanelListCard/index.vue'
import AConfirmLogoutModal from './components/AConfirmLogoutModal/index.vue'
import { useNotificationPanel } from './composables/useNotificationPanel'

import type { Props, Emits } from './type'

import { DisplayMode } from '@/constants/enums/NotificationPanelDisplayMode'
import { NotificationType as NotificationTypeEnum } from '@/constants/enums/NotificationType'
import { formatUserLabel } from '@/utils/User'
import { isPast } from '@/utils/date'
import { useDate } from '@/composables/useDate'
import { useModal } from '@/composables/useModal'
import APanel from '@/components/atoms/APanel/index.vue'
import ABaseIcon from '@/components/atoms/ABaseIcon/index.vue'
import AButton from '@/components/atoms/AButton/index.vue'
import IconLeftArrow from '@/assets/images/icon/arrow_left.svg'

/**
 * Props
 */
const props = defineProps<Props>()

/**
 * Emits
 */
const emit = defineEmits<Emits>()

/**
 * Constants
 */
const PANEL_WIDTH = 350
const CURRENT_DATE = new Date()

/**
 * Composables
 */
const {
  isOpened: isOpenedConfirmLogoutModal,
  open: openConfirmLogoutModal,
  close: closeConfirmLogoutModal,
} = useModal()
const {
  currentNotificationMeta,
  displayedNotificationMetaList,
  toggleUnreadOnlyDisplay,
  changeDetailDisplay,
  changeDisplayMode,
  confirmOpenLink,
  openLinkAndLogout,
} = useNotificationPanel(
  props,
  emit,
  openConfirmLogoutModal,
)
const { formatDateTime, formatDate } = useDate()

/**
 * Computed
 */
const displayUnreadCount = computed(() => {
  const maxDisplayCount = 10
  const displayCap = maxDisplayCount - 1
  return props.unreadCount > displayCap
    ? `(${displayCap}+)`
    : `(${props.unreadCount})`
})
</script>

<template>
  <APanel
    v-bind="$attrs"
    :width="PANEL_WIDTH"
    class="company-memo-panel"
    @close="emit('close')"
  >
    <template #title>
      <span class="title">{{ $t('common.notification.title') }}</span>
    </template>
    <template #content>
      <div class="content">
        <div
          v-if="props.displayMode === DisplayMode.LIST"
          class="filter-section"
        >
          <button
            class="filter-button all-button"
            :class="{ active: !props.isUnreadOnlyDisplay }"
            @click="toggleUnreadOnlyDisplay"
          >
            {{ $t("common.all") }}
          </button>
          <button
            class="filter-button unread-button"
            :class="{ active: props.isUnreadOnlyDisplay }"
            @click="toggleUnreadOnlyDisplay"
          >
            {{ $t('common.notification.unread') }}
            <span v-if="props.unreadCount > 0">{{ displayUnreadCount }}</span>
          </button>
        </div>
        <div
          v-if="props.displayMode === DisplayMode.LIST"
          class="notification-list-section"
        >
          <div class="notification-list">
            <div
              v-if="displayedNotificationMetaList.length === 0"
              class="empty-message-area"
            >
              <div class="empty-message-area">
                <p>{{ $t("common.notification.empty_description_1") }}</p>
                <p>{{ $t("common.notification.empty_description_2") }}</p>
              </div>
            </div>
            <div
              v-for="notification in displayedNotificationMetaList"
              :key="notification.id"
              class="notification-item"
            >
              <ANotificationPanelListCard
                :notification-meta="notification"
                :is-detail="undefined as any"
                @refresh="emit('refresh')"
                @after-click="changeDetailDisplay(notification)"
              />
            </div>
          </div>
        </div>
        <div
          v-else-if="props.displayMode === DisplayMode.DETAIL && currentNotificationMeta"
          class="notification-detail-section"
        >
          <button
            class="back-section"
            @click="changeDisplayMode(DisplayMode.LIST)"
          >
            <ABaseIcon :src="IconLeftArrow" />{{ $t('common.link.back') }}
          </button>
          <div class="notification-detail">
            <div class="notification-detail-top">
              <ANotificationPanelListCard
                :notification-meta="currentNotificationMeta"
                :is-detail="true"
              />
            </div>
          </div>
          <div class="notification-detail-bottom">
            <!-- 回答差し戻し -->
            <div
              v-if="currentNotificationMeta.notificationType === NotificationTypeEnum.ANSWER_REJECTION_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.buyer') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.upload.title') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.questionGroupName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.answer_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDate(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('buyer_scorecard.answer_rejection_modal_text') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.answerGroupRejectionComment }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/upload/common?questionGroupByCompanyId=${currentNotificationMeta.meta.questionGroupByCompanyId}`"
                  @click="emit('close')"
                >
                  {{ $t('question_group_section.answer') }}
                </AButton>
              </div>
            </div>
            <!-- 回答取り下げ依頼 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.RE_ANSWER_REQUEST_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.supplier') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.upload.title') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.questionGroupName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.answer_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDate(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.withdraw.comment_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.reAnswerRequestComment }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/scorecard?supplierRegistTemplateId=${currentNotificationMeta.meta.supplierRegistTemplateId}`"
                  @click="() => {
                    emit('close')
                  }"
                >
                  {{ $t('common.btn.confirm_action') }}
                </AButton>
              </div>
            </div>
            <!-- 回答スコアCSVダウンロード成功 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.CSV_DOWNLOAD_SUCCESS_MAIL"
              class="notification-detail-item"
            >
              <div>
                <AButton
                  :href="currentNotificationMeta.meta.downloadUrl"
                  download
                  :disabled="isPast(CURRENT_DATE)(currentNotificationMeta.meta.expiresDate)"
                >
                  {{ $t('common.btn.download') }}
                </AButton>
              </div>
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.evaluation_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDateTime(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- 回答スコアPDFダウンロード成功 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.PDF_DOWNLOAD_SUCCESS_MAIL"
              class="notification-detail-item"
            >
              <div>
                <AButton
                  :href="currentNotificationMeta.meta.downloadUrl"
                  target="_blank"
                  :disabled="isPast(CURRENT_DATE)(currentNotificationMeta.meta.expiresDate)"
                >
                  {{ $t('common.btn.download') }}
                </AButton>
              </div>
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.evaluation_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDateTime(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- 添付ファイル一括ダウンロード成功 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.ATTACH_FILE_DOWNLOAD_SUCCESS_MAIL"
              class="notification-detail-item"
            >
              <div
                v-for="(downloadUrl, index) in currentNotificationMeta.meta.downloadUrls"
                :key="downloadUrl"
                class="download-section"
              >
                <div
                  v-if="currentNotificationMeta.meta.downloadUrls.length > 1"
                >
                  {{ $t('common.notification.download.file_number', { number: index + 1 }) }}
                </div>

                <AButton
                  :href="downloadUrl"
                  download
                  :disabled="isPast(CURRENT_DATE)(currentNotificationMeta.meta.expiresDate)"
                  class="download-section-button"
                >
                  {{ $t('common.btn.download') }}<template v-if="currentNotificationMeta.meta.downloadUrls.length > 1">
                    {{ index + 1 }}
                  </template>
                </AButton>
              </div>
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.evaluation_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDateTime(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- 改善計画メッセージ（バイヤー→サプライヤー） -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_BUYER"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.company_name_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.message_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.message }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/improvementplans/detail?evaluationId=${currentNotificationMeta.meta.evaluationId}&questionGroupId=${currentNotificationMeta.meta.questionGroupId}&indicatedPointId=${currentNotificationMeta.meta.indicatedPointId}`"
                  @click="emit('close')"
                >
                  {{ $t('common.btn.confirm_action') }}
                </AButton>
              </div>
            </div>
            <!-- 改善計画メッセージ（サプライヤー→バイヤー） -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.IMPROVEMENT_PLAN_MAIL_FROM_SUPPLIER"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.company_name_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.message_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.message }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/improvementplans/detail?evaluationId=${currentNotificationMeta.meta.evaluationId}&supplierRegistTemplateId=${currentNotificationMeta.meta.supplierRegistTemplateId}&questionGroupId=${currentNotificationMeta.meta.questionGroupId}&indicatedPointId=${currentNotificationMeta.meta.indicatedPointId}`"
                  @click="emit('close')"
                >
                  {{ $t('common.btn.confirm_action') }}
                </AButton>
              </div>
            </div>
            <!-- ESGデータシェア招待 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.ESG_DATA_INVITATION_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.data_share_invitation.person_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatUserLabel({
                      userName: currentNotificationMeta.meta.userName,
                      email: currentNotificationMeta.meta.email,
                    }) }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.company_name_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.data_share_invitation.data_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.esgDataName }}
                  </div>
                </div>
                <div
                  v-if="currentNotificationMeta.meta.message"
                  class="detail-item-row"
                >
                  <div class="detail-item-label">
                    {{ $t('common.notification.message_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.message }}
                  </div>
                </div>
              </div>
              <div>
                <p>{{ $t('common.notification.data_share_invitation.description_1') }}</p>
                <a
                  class="link"
                  @click="confirmOpenLink(currentNotificationMeta.meta.url)"
                >
                  {{ currentNotificationMeta.meta.url }}
                </a>
              </div>
              <div>
                <p>※{{ $t('common.notification.data_share_invitation.description_2') }}</p>
              </div>
            </div>
            <!-- ESGデータシェア招待完了 -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.ESG_DATA_INVITATION_COMPLETE_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.data_share_invitation.person_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatUserLabel({
                      userName: currentNotificationMeta.meta.userName,
                      email: currentNotificationMeta.meta.email,
                    }) }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.company_name_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.data_share_invitation.data_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.esgDataName }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/buyer/datashare/detail?esgDataAnswerGroupVersionId=${currentNotificationMeta.meta.esgDataAnswerGroupVersionId}`"
                  @click="emit('close')"
                >
                  {{ $t('common.btn.confirm_action') }}
                </AButton>
              </div>
            </div>
            <!-- 回答リマインド（バイヤー） -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.BUYER_REMIND_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.notification.company_name_label') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.companyName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.upload.title') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.questionGroupName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.answer_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDate(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/upload/common?questionGroupByCompanyId=${currentNotificationMeta.meta.questionGroupByCompanyId}`"
                  @click="emit('close')"
                >
                  {{ $t('question_group_section.answer') }}
                </AButton>
              </div>
            </div>
            <!-- 回答リマインド（サプライヤー） -->
            <div
              v-else-if="currentNotificationMeta.notificationType === NotificationTypeEnum.ASUENE_REMIND_MAIL"
              class="notification-detail-item"
            >
              <div class="detail-item-list">
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.upload.title') }}
                  </div>
                  <div class="detail-item-value">
                    {{ currentNotificationMeta.meta.questionGroupName }}
                  </div>
                </div>
                <div class="detail-item-row">
                  <div class="detail-item-label">
                    {{ $t('common.date.answer_expires_date') }}
                  </div>
                  <div class="detail-item-value">
                    {{ formatDate(currentNotificationMeta.meta.expiresDate) }}
                  </div>
                </div>
              </div>
              <div class="button-section">
                <AButton
                  :to="`/mypage/upload/common?questionGroupByCompanyId=${currentNotificationMeta.meta.questionGroupByCompanyId}`"
                  @click="emit('close')"
                >
                  {{ $t('question_group_section.answer') }}
                </AButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </APanel>
  <Teleport to="body">
    <AConfirmLogoutModal
      :is-opened="isOpenedConfirmLogoutModal"
      @cancel="closeConfirmLogoutModal"
      @confirm="openLinkAndLogout"
    />
  </Teleport>
</template>

<style scoped>
  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;

    /* 上部セクションと余白を考慮した値を引き、スクロールが下部余白まで届くようにしている */
    height: calc(100% - 61px);
    padding: 20px;
    overflow: auto;

    .empty-message-area {
      color: rgb(var(--v-theme-ui-text-gray-1));
    }

    .filter-section {
      display: flex;
      justify-content: end;

      .filter-button {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 72px;
        height: 21px;
        font-size: 14px;
        font-weight: 600;
        color: rgb(var(--v-theme-ui-text-gray-1));
        border: 1px solid rgb(var(--v-theme-ui-gray1));
      }

      .all-button {
        border-right: none;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      .unread-button {
        border-left: none;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      .active {
        color: rgb(var(--v-theme-ui-white));
        background-color: rgb(var(--v-theme-primary));
      }
    }

    .notification-list-section {
      .notification-list {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
    }

    .notification-detail-section {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .back-section {
        display: flex;
        align-items: center;

        &:hover {
          opacity: 0.7;
        }
      }

      .notification-detail-item {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .download-link, .link {
          font-size: 14px;
          color: rgb(var(--v-theme-primary));
          text-decoration: underline;
          cursor: pointer;
        }

        .download-section {
          display: grid;
          gap: 8px;
        }

        .download-section + .download-section {
          margin-top: 8px;
        }

        .download-section > * {
          min-width: 0;
        }

        .download-section > .download-section-button {
          width: min-content;
        }

        .detail-item-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          overflow-wrap: break-word;
          white-space: pre-wrap;

          .detail-item-row {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .detail-item-label {
            font-size: 12px;
            color: rgb(var(--v-theme-ui-text-gray-1));
          }
        }

        .button-section {
          display: flex;
          justify-content: end;
          margin-top: 8px;
        }
      }

    }
  }

  .content::-webkit-scrollbar {
    display: none;
  }
</style>
